import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Divider,
  InputAdornment,
  useTheme,
  TablePagination,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  AddOutlined,
  CheckCircleOutline,
  RefreshOutlined,
  VisibilityOutlined,
  SendOutlined,
  VerifiedUserOutlined,
  RemoveCircleOutline,
  CalculateOutlined,
  ReceiptLongOutlined,
  InfoOutlined,
  AutoModeOutlined,
  DeleteOutline,
  EditOutlined,
  MoreVertOutlined,
  FileCopyOutlined,
} from "@mui/icons-material";
import { tokens } from "../../theme";
import Swal from "sweetalert2";
import { formatKES } from "../WalletManagement/WalletBalance";
import {
  walletQuery,
  walletMutate,
  FEE_SCHEDULES_QUERY,
  ACTIVE_FEE_SCHEDULE_QUERY,
  FEE_TIERS_QUERY,
  CALCULATE_CHARGES_QUERY,
  CREATE_FEE_SCHEDULE_MUTATION,
  UPSERT_FEE_TIERS_MUTATION,
  SUBMIT_FEE_SCHEDULE_FOR_APPROVAL_MUTATION,
  APPROVE_FEE_SCHEDULE_MUTATION,
  RETIRE_FEE_SCHEDULE_MUTATION,
} from "../../data/WalletService/walletGraphQL";
import { loadUsersMap, getUserDisplayName } from "./userResolver";

const DisbursementFeeSchedules = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  // User Display Names Map
  const [userMap, setUserMap] = useState(new Map());

  useEffect(() => {
    let mounted = true;
    loadUsersMap().then((map) => {
      if (mounted && map) setUserMap(new Map(map));
    });
    return () => {
      mounted = false;
    };
  }, []);

  // Data State
  const [schedules, setSchedules] = useState([]);
  const [activeSchedule, setActiveSchedule] = useState(null);
  const [activeTiers, setActiveTiers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Pagination State for Fee Schedules
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Live Calculator State
  const [calcAmount, setCalcAmount] = useState(5000);
  const [calcResult, setCalcResult] = useState(null);
  const [calculating, setCalculating] = useState(false);

  // View Tiers Dialog
  const [viewSchedule, setViewSchedule] = useState(null);
  const [viewTiers, setViewTiers] = useState([]);
  const [viewLoading, setViewLoading] = useState(false);

  // Create Schedule Modal State
  const [createOpen, setCreateOpen] = useState(false);
  const [newScheduleName, setNewScheduleName] = useState("");
  const [newEffectiveFrom, setNewEffectiveFrom] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [newNotes, setNewNotes] = useState("");
  const [newTiers, setNewTiers] = useState([]);
  const [creating, setCreating] = useState(false);
  // When set, the modal is in "edit" mode for this schedule ID
  const [editingId, setEditingId] = useState(null);
  const [editingStatus, setEditingStatus] = useState(null);

  // Row Actions Menu
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const closeMenu = () => setMenuAnchor(null);
  const isEditing = editingId !== null;

  // Fetch Schedules & Active Schedule
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [allRes, activeRes] = await Promise.allSettled([
        walletQuery(FEE_SCHEDULES_QUERY),
        walletQuery(ACTIVE_FEE_SCHEDULE_QUERY),
      ]);

      if (allRes.status === "fulfilled" && allRes.value?.feeSchedules) {
        setSchedules(allRes.value.feeSchedules);
      } else {
        setSchedules([]);
      }

      if (activeRes.status === "fulfilled" && activeRes.value?.activeFeeSchedule) {
        const active = activeRes.value.activeFeeSchedule;
        setActiveSchedule(active);
        // Fetch tiers for active schedule
        try {
          const tiersRes = await walletQuery(FEE_TIERS_QUERY, { scheduleId: parseInt(active.id, 10) });
          if (tiersRes?.feeTiers) {
            setActiveTiers(tiersRes.feeTiers);
          }
        } catch (tErr) {
          console.warn("[DisbursementFees] Failed loading active tiers:", tErr);
          setActiveTiers([]);
        }
      } else {
        setActiveSchedule(null);
        setActiveTiers([]);
      }
    } catch (err) {
      console.error("[DisbursementFees] Load error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Live Charge Calculator — setup-driven from backend or active setup tiers
  const handleCalculateCharge = useCallback(async (amt) => {
    const numericAmt = parseFloat(amt);
    if (isNaN(numericAmt) || numericAmt <= 0) {
      setCalcResult(null);
      return;
    }
    setCalculating(true);
    try {
      const res = await walletQuery(CALCULATE_CHARGES_QUERY, { amount: numericAmt });
      if (res?.calculateCharges) {
        setCalcResult(res.calculateCharges);
        return;
      }
    } catch {
      // Backend calculation unavailable; evaluate against active setup tiers
    } finally {
      setCalculating(false);
    }

    // Match against active schedule tiers configured in setups
    if (activeTiers && activeTiers.length > 0) {
      const matched = activeTiers.find(
        (t) =>
          numericAmt >= Number(t.amountFrom) &&
          (t.amountTo == null || numericAmt <= Number(t.amountTo))
      );
      if (matched) {
        setCalcResult({
          sababishaCharge: matched.sababishaCharge,
          mpesaCharge: matched.mpesaCharge,
          totalCharge:
            matched.totalCharge ??
            (Number(matched.sababishaCharge || 0) + Number(matched.mpesaCharge || 0)),
          tierId: matched.id,
          scheduleId: activeSchedule?.id,
        });
        return;
      }
    }

    setCalcResult(null);
  }, [activeTiers, activeSchedule]);

  useEffect(() => {
    if (calcAmount) {
      handleCalculateCharge(calcAmount);
    }
  }, [calcAmount, handleCalculateCharge]);

  // View Schedule Tiers Modal
  const handleOpenViewTiers = async (schedule) => {
    setViewSchedule(schedule);
    setViewLoading(true);
    try {
      const res = await walletQuery(FEE_TIERS_QUERY, { scheduleId: parseInt(schedule.id, 10) });
      setViewTiers(res?.feeTiers || []);
    } catch (err) {
      console.error("[DisbursementFees] Failed to fetch schedule tiers:", err);
      setViewTiers([]);
    } finally {
      setViewLoading(false);
    }
  };

  // Submit Schedule For Approval (Maker Step)
  const handleSubmitForApproval = async (scheduleId) => {
    const result = await Swal.fire({
      title: "Submit for Approval?",
      text: `Submit Disbursement Fee Schedule #${scheduleId} for checker review? It will move to PENDING_APPROVAL.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#1766A2",
      confirmButtonText: "Submit for Approval",
    });

    if (result.isConfirmed) {
      try {
        await walletMutate(SUBMIT_FEE_SCHEDULE_FOR_APPROVAL_MUTATION, { scheduleId: parseInt(scheduleId, 10) });
        Swal.fire("Submitted", "Disbursement fee schedule submitted for checker approval.", "success");
        loadData();
      } catch (err) {
        Swal.fire("Action Failed", err.message || "Failed to submit fee schedule.", "error");
      }
    }
  };

  // Approve Schedule (Checker Step - Maker ≠ Checker)
  const handleApproveSchedule = async (scheduleId) => {
    const result = await Swal.fire({
      title: "Approve Fee Schedule?",
      text: `Approve Disbursement Fee Schedule #${scheduleId}? This schedule will become ACTIVE and govern all loan disbursements platform-wide. (Maker ≠ Checker enforced).`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#22bf72",
      confirmButtonText: "Approve & Activate",
    });

    if (result.isConfirmed) {
      try {
        await walletMutate(APPROVE_FEE_SCHEDULE_MUTATION, { scheduleId: parseInt(scheduleId, 10) });
        Swal.fire("Approved & Activated", "Fee schedule is now ACTIVE.", "success");
        loadData();
      } catch (err) {
        Swal.fire("Approval Failed", err.message || "Maker-checker policy rejected this approval.", "error");
      }
    }
  };

  // Retire Schedule — retirement is permanent for audit immutability
  const handleRetireSchedule = async (scheduleId) => {
    const result = await Swal.fire({
      title: "Retire Active Schedule?",
      html: `
        <div style="text-align: left; font-size: 0.9rem;">
          <p>Are you sure you want to retire <strong>Disbursement Fee Schedule #${scheduleId}</strong>?</p>
          <div style="background: #fff3cd; color: #856404; padding: 10px; border-radius: 6px; margin: 10px 0; font-size: 0.85rem; border: 1px solid #ffeeba;">
            <strong>Notice: Retirement is permanent.</strong> Retired schedules cannot be reactivated directly. They are permanently archived for historical ledger audit trails. To use these tariff rates again, you can clone them into a new draft schedule.
          </div>
          <p style="color: #666; font-size: 0.8rem;">Platform disbursements will require an active schedule.</p>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Confirm Permanent Retirement",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        await walletMutate(RETIRE_FEE_SCHEDULE_MUTATION, { scheduleId: parseInt(scheduleId, 10) });
        Swal.fire({
          icon: "success",
          title: "Schedule Retired",
          text: `Disbursement fee schedule #${scheduleId} has been permanently retired and archived.`,
        });
        loadData();
      } catch (err) {
        Swal.fire("Retirement Failed", err.message || "Failed to retire schedule.", "error");
      }
    }
  };

  // Tier Editor Handlers in Create Modal
  const handleTierChange = (index, field, value) => {
    let cleanVal = value;
    // Allow empty string or valid numeric digits with optional decimal point
    if (cleanVal !== "" && !/^\d*\.?\d*$/.test(cleanVal)) {
      return;
    }
    if (cleanVal !== "") {
      // If user typed e.g. "06", strip leading zero so it becomes "6" (preserve "0." for decimals)
      if (/^0\d/.test(cleanVal)) {
        cleanVal = cleanVal.replace(/^0+(?=\d)/, "");
      }
    }

    setNewTiers((prev) => {
      const copy = [...prev];
      copy[index] = {
        ...copy[index],
        [field]: cleanVal,
      };
      if (field === "sababishaCharge" || field === "mpesaCharge") {
        const sVal = parseFloat(field === "sababishaCharge" ? cleanVal : copy[index].sababishaCharge) || 0;
        const mVal = parseFloat(field === "mpesaCharge" ? cleanVal : copy[index].mpesaCharge) || 0;
        copy[index].totalCharge = sVal + mVal;
      }
      return copy;
    });
  };

  const handleAddTierRow = () => {
    const lastTier = newTiers[newTiers.length - 1];
    const prevTo = lastTier ? (parseFloat(lastTier.amountTo) || 0) : 0;
    const nextFrom = lastTier ? prevTo + 1 : 0;
    const defaultSpan = prevTo <= 1000 ? 400 : 10000;
    setNewTiers((prev) => [
      ...prev,
      {
        amountFrom: String(nextFrom),
        amountTo: String(nextFrom + defaultSpan),
        sababishaCharge: "0",
        mpesaCharge: "0",
        totalCharge: 0,
      },
    ]);
  };

  const handleRemoveTierRow = (index) => {
    setNewTiers((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCloneActiveTiers = () => {
    if (activeTiers && activeTiers.length > 0) {
      setNewTiers(
        activeTiers.map((t) => ({
          amountFrom: t.amountFrom,
          amountTo: t.amountTo,
          sababishaCharge: t.sababishaCharge,
          mpesaCharge: t.mpesaCharge,
          totalCharge:
            t.totalCharge ??
            (Number(t.sababishaCharge || 0) + Number(t.mpesaCharge || 0)),
        }))
      );
      Swal.fire({
        icon: "success",
        title: "Active Tiers Loaded",
        text: `Populated ${activeTiers.length} bands from active Schedule #${activeSchedule?.id}.`,
        timer: 1500,
        showConfirmButton: false,
      });
    } else {
      Swal.fire({
        icon: "info",
        title: "No Active Schedule",
        text: "There is no active schedule with configured bands to clone from.",
      });
    }
  };

  // Create & Upsert Schedule
  const handleCreateSchedule = async (e) => {
    if (e) e.preventDefault();
    if (!newScheduleName.trim()) {
      Swal.fire("Validation Error", "Please provide a schedule name.", "warning");
      return;
    }
    if (newTiers.length === 0) {
      Swal.fire("Validation Error", "Please configure at least one fee tier band.", "warning");
      return;
    }

    // First band must start at 0
    const firstTierFrom = parseFloat(newTiers[0]?.amountFrom) || 0;
    if (firstTierFrom !== 0) {
      Swal.fire("Validation Error", "The first tariff band must start at 0 KES.", "warning");
      return;
    }

    // Check that each band's amountTo > amountFrom
    for (let i = 0; i < newTiers.length; i++) {
      const from = parseFloat(newTiers[i].amountFrom) || 0;
      const to = parseFloat(newTiers[i].amountTo) || 0;
      if (to <= from) {
        Swal.fire(
          "Validation Error",
          `Band ${i + 1} 'Band To' (${to.toLocaleString()} KES) must be greater than 'Band From' (${from.toLocaleString()} KES).`,
          "warning"
        );
        return;
      }
    }

    // Check contiguity between consecutive bands
    for (let i = 0; i < newTiers.length - 1; i++) {
      const currentTo = parseFloat(newTiers[i].amountTo) || 0;
      const nextFrom = parseFloat(newTiers[i + 1].amountFrom) || 0;
      if (nextFrom !== currentTo && nextFrom !== currentTo + 1) {
        Swal.fire(
          "Validation Error",
          `Band continuity error between band ${i + 1} (ends at ${currentTo.toLocaleString()} KES) and band ${i + 2} (starts at ${nextFrom.toLocaleString()} KES). Bands must be contiguous.`,
          "warning"
        );
        return;
      }
    }

    // The platform limit must reach exactly 1,000,000 KES (cannot proceed if below 1,000,000)
    const lastTier = newTiers[newTiers.length - 1];
    const maxAmountTo = parseFloat(lastTier?.amountTo) || 0;
    if (maxAmountTo < 1000000) {
      Swal.fire(
        "Validation Error",
        `The maximum tariff limit must be 1,000,000 KES (current max is ${maxAmountTo.toLocaleString()} KES). Please configure the final band to end at 1,000,000 KES before saving.`,
        "warning"
      );
      return;
    }
    if (maxAmountTo > 1000000) {
      Swal.fire(
        "Validation Error",
        `The maximum tariff limit cannot exceed 1,000,000 KES (current max is ${maxAmountTo.toLocaleString()} KES). The final band must end at 1,000,000 KES.`,
        "warning"
      );
      return;
    }

    // Editing a live schedule sends it back through maker-checker: confirm first
    if (isEditing && editingStatus === "ACTIVE") {
      const confirm = await Swal.fire({
        title: "Edit Active Schedule?",
        text: `Saving changes to Schedule #${editingId} will move it to PENDING_APPROVAL and it will need checker approval before it governs disbursements again.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#1766A2",
        confirmButtonText: "Save & Submit for Approval",
      });
      if (!confirm.isConfirmed) return;
    }

    setCreating(true);
    try {
      const scheduleInput = {
        name: newScheduleName,
        effectiveFrom: newEffectiveFrom ? new Date(newEffectiveFrom).toISOString() : new Date().toISOString(),
        notes: newNotes,
      };

      let scheduleId;
      if (isEditing) {
        // 1. Edit mode: schedule header is unchanged; only the bands are upserted below
        scheduleId = editingId;
      } else {
        // 1. Create Schedule
        const createRes = await walletMutate(CREATE_FEE_SCHEDULE_MUTATION, {
          input: scheduleInput,
        });
        scheduleId = createRes?.createFeeSchedule;
        if (!scheduleId) {
          throw new Error("Failed to receive created schedule.");
        }
      }

      // 2. Upsert Tiers
      const formattedTiers = newTiers.map((t) => ({
        amountFrom: parseFloat(t.amountFrom) || 0,
        amountTo: parseFloat(t.amountTo) || 0,
        sababishaCharge: parseFloat(t.sababishaCharge) || 0,
        mpesaCharge: parseFloat(t.mpesaCharge) || 0,
      }));

      await walletMutate(UPSERT_FEE_TIERS_MUTATION, {
        input: {
          scheduleId: parseInt(scheduleId, 10),
          tiers: formattedTiers,
        },
      });

      // Edit mode: the backend upsert moves the schedule to PENDING_APPROVAL itself

      Swal.fire({
        icon: "success",
        title: isEditing ? "Schedule Updated" : "Schedule Created (Draft)",
        text: isEditing
          ? `Disbursement schedule #${scheduleId} has been updated with ${newTiers.length} tariff bands and is now PENDING_APPROVAL.`
          :`Disbursement schedule #${scheduleId} has been created with ${newTiers.length} tariff bands. You can now submit it for approval.`,
      });

      setCreateOpen(false);
      setEditingId(null);
      setNewScheduleName("");
      setNewNotes("");
      setNewTiers([]);
      loadData();
    } catch (err) {
      console.error("[DisbursementFees] Save error:", err);
      Swal.fire(
        isEditing ? "Update Failed" : "Creation Failed",
        err.message || "Could not save fee schedule.",
        "error"
      );
    } finally {
      setCreating(false);
    }
  };

  const getStatusChip = (status) => {
    switch (status) {
      case "ACTIVE":
        return <Chip label="ACTIVE" color="success" size="small" sx={{ fontWeight: 700 }} />;
      case "PENDING_APPROVAL":
        return <Chip label="PENDING APPROVAL" color="warning" size="small" sx={{ fontWeight: 700 }} />;
      case "DRAFT":
        return <Chip label="DRAFT" color="info" size="small" sx={{ fontWeight: 700 }} />;
      case "RETIRED":
        return (
          <Tooltip title="Permanent archive: retired schedules cannot be directly reactivated. Use 'Clone as New Draft' to create a new version.">
            <Chip label="RETIRED" color="default" size="small" sx={{ fontWeight: 700 }} />
          </Tooltip>
        );
      default:
        return <Chip label={status || "UNKNOWN"} size="small" />;
    }
  };

  // Clone a retired schedule into a fresh draft schedule (preserves permanent retirement audit)
  const handleOpenCloneModal = async (schedule) => {
    setEditingId(null);
    setEditingStatus(null);
    setNewScheduleName(`${schedule.name || "Disbursement Schedule"} (New Version)`);
    setNewNotes(`Cloned from retired schedule #${schedule.id}. ${schedule.notes || ""}`.trim());
    setNewEffectiveFrom(new Date().toISOString().slice(0, 10));
    setNewTiers([]);
    setCreateOpen(true);
    try {
      const res = await walletQuery(FEE_TIERS_QUERY, { scheduleId: parseInt(schedule.id, 10) });
      setNewTiers(
        (res?.feeTiers || []).map((t) => ({
          amountFrom: String(t.amountFrom ?? "0"),
          amountTo: String(t.amountTo ?? ""),
          sababishaCharge: String(t.sababishaCharge ?? "0"),
          mpesaCharge: String(t.mpesaCharge ?? "0"),
          totalCharge:
            t.totalCharge ?? (Number(t.sababishaCharge || 0) + Number(t.mpesaCharge || 0)),
        }))
      );
    } catch (err) {
      console.error("[DisbursementFees] Failed to load tiers for clone:", err);
      Swal.fire("Load Failed", "Could not load the tariff bands from the retired schedule.", "error");
    }
  };

  // Open the modal pre-filled with an existing schedule (edit mode)
  const handleOpenEditModal = async (schedule) => {
    setEditingId(schedule.id);
    setEditingStatus(schedule.status);
    setNewScheduleName(schedule.name || "");
    setNewNotes(schedule.notes || "");
    setNewEffectiveFrom(
      schedule.effectiveFrom
        ? new Date(schedule.effectiveFrom).toISOString().slice(0, 10)
        : new Date().toISOString().slice(0, 10)
    );
    setNewTiers([]);
    setCreateOpen(true);
    try {
      const res = await walletQuery(FEE_TIERS_QUERY, { scheduleId: parseInt(schedule.id, 10) });
      setNewTiers(
        (res?.feeTiers || []).map((t) => ({
          amountFrom: String(t.amountFrom ?? "0"),
          amountTo: String(t.amountTo ?? ""),
          sababishaCharge: String(t.sababishaCharge ?? "0"),
          mpesaCharge: String(t.mpesaCharge ?? "0"),
          totalCharge:
            t.totalCharge ?? (Number(t.sababishaCharge || 0) + Number(t.mpesaCharge || 0)),
        }))
      );
    } catch (err) {
      console.error("[DisbursementFees] Failed to load tiers for edit:", err);
      Swal.fire("Load Failed", "Could not load the tariff bands for this schedule.", "error");
    }
  };

  const handleCloseModal = () => {
    setCreateOpen(false);
    setEditingId(null);
    setEditingStatus(null);
  };

  const handleOpenCreateModal = () => {
    setEditingId(null);
    setEditingStatus(null);
    setNewScheduleName("");
    setNewNotes("");
    setNewEffectiveFrom(new Date().toISOString().slice(0, 10));
    setNewTiers([
      { amountFrom: "0", amountTo: "1000", sababishaCharge: "0", mpesaCharge: "0", totalCharge: 0 },
    ]);
    setCreateOpen(true);
  };

  return (
    <Box sx={{ width: "100%" }}>
      {/* ── Active Schedule & Charge Transparency Banner ─────────────────── */}
      <Card
        sx={{
          mb: 3,
          borderRadius: 2.5,
          background: isDark
            ? "linear-gradient(135deg, rgba(23,102,162,0.2) 0%, rgba(34,191,114,0.15) 100%)"
            : "linear-gradient(135deg, #f0f7ff 0%, #f0fdf4 100%)",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#cbd5e1"}`,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2 }}>
            <Box>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                <ReceiptLongOutlined sx={{ color: "#1766A2", fontSize: 26 }} />
                <Typography variant="h6" sx={{ fontWeight: 800, color: colors.grey[100] }}>
                  Loan Disbursement Fee Schedule (Standard Tariff)
                </Typography>
                {activeSchedule ? (
                  <Chip
                    icon={<CheckCircleOutline fontSize="small" />}
                    label={`Active Schedule #${activeSchedule.id}: ${activeSchedule.name}`}
                    color="success"
                    size="small"
                    sx={{ fontWeight: 700 }}
                  />
                ) : (
                  <Chip label="No Active Schedule" color="warning" size="small" sx={{ fontWeight: 700 }} />
                )}
              </Stack>
              <Typography variant="body2" sx={{ color: colors.grey[400], maxWidth: 800 }}>
                Disbursement charges follow a tiered tariff by transaction value. Every band states what{" "}
                <strong>Safaricom charges</strong>, what <strong>Sababisha charges</strong>, and the <strong>Total</strong>,
                so the company can see exactly how a charge is made up.
              </Typography>
            </Box>

            <Button
              variant="contained"
              size="small"
              startIcon={<AddOutlined />}
              onClick={handleOpenCreateModal}
              sx={{
                backgroundColor: "#1766A2",
                color: "#fff",
                fontWeight: 700,
                textTransform: "none",
                px: 2.5,
                borderRadius: 2,
              }}
            >
              Create Disbursement Schedule
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* ── Interactive Live Charge Calculator & Tariff Breakdown ─────────── */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Real-time Transparency Calculator */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              height: "100%",
              borderRadius: 2.5,
              backgroundColor: isDark ? colors.primary[400] : "#ffffff",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <CalculateOutlined sx={{ color: "#1766A2" }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  Disbursement Fee Calculator
                </Typography>
              </Stack>
              <Typography variant="caption" sx={{ color: colors.grey[400], display: "block", mb: 2 }}>
                Test any loan disbursement value to inspect the exact fee breakdown across Safaricom and Sababisha.
              </Typography>

              <TextField
                fullWidth
                label="Loan Principal Amount (KES)"
                type="number"
                size="small"
                value={calcAmount}
                onChange={(e) => setCalcAmount(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">KES</InputAdornment>,
                  endAdornment: calculating ? <CircularProgress size={16} /> : null,
                }}
                sx={{ mb: 2.5 }}
              />

              {calcResult && (
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: isDark ? "rgba(0,0,0,0.25)" : "#f8fafc",
                    border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700, color: colors.grey[400], display: "block", mb: 1.5 }}>
                    CHARGE TRANSPARENCY BREAKDOWN
                  </Typography>

                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="body2" sx={{ color: colors.grey[300] }}>
                      Sababisha Charge:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#1766A2" }}>
                      {formatKES(calcResult.sababishaCharge)}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="body2" sx={{ color: colors.grey[300] }}>
                      Safaricom (M-Pesa) Charge:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#ed6c02" }}>
                      {formatKES(calcResult.mpesaCharge)}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 1 }} />

                                   <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      Total Debited Charge:
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#22bf72" }}>
                      {formatKES(calcResult.totalCharge)}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1.5, pt: 1, borderTop: "1px dashed rgba(255,255,255,0.1)" }}>
                    <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                      Amount Received by Borrower:
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: colors.grey[100] }}>
                      {formatKES(parseFloat(calcAmount || 0))}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      Total Deducted from Wallet:
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#ed6c02" }}>
                      {formatKES(parseFloat(calcAmount || 0) + calcResult.totalCharge)}
                    </Typography>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Active Tariff Matrix Table */}
        <Grid item xs={12} md={8}>
          <Card
            sx={{
              borderRadius: 2.5,
              backgroundColor: isDark ? colors.primary[400] : "#ffffff",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    Active Disbursement Tariff Bands
                  </Typography>
                  <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                    {activeSchedule
                      ? `Viewing ${activeTiers.length} active fee tiers from Schedule #${activeSchedule.id} (${activeSchedule.name})`
                      : "No active disbursement fee schedule configured in setups"}
                  </Typography>
                </Box>
                <Tooltip title="Refresh schedule and tiers">
                  <IconButton size="small" onClick={loadData} disabled={loading}>
                    {loading ? <CircularProgress size={16} /> : <RefreshOutlined fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </Box>

              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : !activeSchedule || activeTiers.length === 0 ? (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 5,
                    px: 2,
                    borderRadius: 2,
                    border: `1px dashed ${isDark ? "rgba(255,255,255,0.12)" : "#cbd5e1"}`,
                  }}
                >
                  <ReceiptLongOutlined sx={{ fontSize: 36, color: colors.grey[400], mb: 1, opacity: 0.6 }} />
                  <Typography variant="body2" sx={{ color: colors.grey[300], fontWeight: 600 }}>
                    No Active Tariff Bands Configured
                  </Typography>
                  <Typography variant="caption" sx={{ color: colors.grey[400], display: "block", mt: 0.5, maxWidth: 460, mx: "auto" }}>
                    There are currently no active disbursement fee schedule bands in setups. Create and approve a fee schedule below to activate tiered pricing for loan disbursements.
                  </Typography>
                </Box>
              ) : (
                <TableContainer sx={{ maxHeight: 310 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Transaction Value Band (KES)</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: "#1766A2" }}>
                          Sababisha Charge
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: "#ed6c02" }}>
                          Safaricom (M-Pesa) Charge
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: "#22bf72" }}>
                          Total Charge
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {activeTiers.map((tier, idx) => (
                        <TableRow key={tier.id || idx} hover>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {formatKES(tier.amountFrom)} - {formatKES(tier.amountTo)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, color: "#1766A2" }}>
                            {formatKES(tier.sababishaCharge)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, color: "#ed6c02" }}>
                            {formatKES(tier.mpesaCharge)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 800, color: "#22bf72" }}>
                            {formatKES(
                              tier.totalCharge ??
                                (Number(tier.sababishaCharge || 0) + Number(tier.mpesaCharge || 0))
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ── Fee Schedule Directory & Approvals Table ─────────────────────── */}
      <Card
        sx={{
          borderRadius: 2.5,
          backgroundColor: isDark ? colors.primary[400] : "#ffffff",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: colors.grey[100] }}>
                Disbursement Fee Schedules & Approval Lifecycle
              </Typography>
              <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                Maker-checker governance: DRAFT &rarr; PENDING_APPROVAL &rarr; ACTIVE (approver &ne; creator).
              </Typography>
            </Box>
            <Tooltip title="Reload fee schedules">
              <IconButton size="small" onClick={loadData}>
                <RefreshOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : schedules.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <Typography variant="body2" sx={{ color: colors.grey[400] }}>
                No disbursement fee schedules found. Click &quot;Create Disbursement Schedule&quot; to seed the first tariff.
              </Typography>
            </Box>
          ) : (<>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>ID</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Schedule Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Effective Date</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Created By</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Approved By</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {schedules
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ fontWeight: 700 }}>#{row.id}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {row.name}
                        </Typography>
                        {row.notes && (
                          <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                            {row.notes}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{getStatusChip(row.status)}</TableCell>
                      <TableCell>
                        {row.effectiveFrom ? new Date(row.effectiveFrom).toLocaleDateString() : "Immediate"}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ display: "block", fontWeight: 600 }}>
                          {getUserDisplayName(row.createdBy, userMap)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                          {row.createdDate ? new Date(row.createdDate).toLocaleDateString() : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {row.approvedBy ? (
                          <>
                            <Typography variant="caption" sx={{ display: "block", color: "#22bf72", fontWeight: 700 }}>
                              {getUserDisplayName(row.approvedBy, userMap)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                              {row.approvedDate ? new Date(row.approvedDate).toLocaleDateString() : ""}
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                            Pending
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Actions">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              setMenuAnchor(e.currentTarget);
                              setMenuRow(row);
                            }}
                          >
                            <MoreVertOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={schedules.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{
                borderTop: `1px solid ${
                  isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"
                }`,
              }}
            />
          </>)}
        </CardContent>
      </Card>

      {/* ── Row Actions Menu ─────────────────────────────────────────────── */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem
          onClick={() => {
            closeMenu();
            handleOpenViewTiers(menuRow);
          }}
        >
          <ListItemIcon><VisibilityOutlined fontSize="small" /></ListItemIcon>
          <ListItemText>View Tariff Bands</ListItemText>
        </MenuItem>
        {menuRow?.status !== "RETIRED" ? (
          <MenuItem
            onClick={() => {
              closeMenu();
              handleOpenEditModal(menuRow);
            }}
          >
            <ListItemIcon><EditOutlined fontSize="small" /></ListItemIcon>
            <ListItemText>Edit (resubmits for approval)</ListItemText>
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => {
              closeMenu();
              handleOpenCloneModal(menuRow);
            }}
          >
            <ListItemIcon><FileCopyOutlined fontSize="small" color="primary" /></ListItemIcon>
            <ListItemText>Clone as New Draft</ListItemText>
          </MenuItem>
        )}
        {menuRow?.status === "DRAFT" && (
          <MenuItem
            onClick={() => {
              closeMenu();
              handleSubmitForApproval(menuRow.id);
            }}
          >
            <ListItemIcon><SendOutlined fontSize="small" color="warning" /></ListItemIcon>
            <ListItemText>Submit for Approval</ListItemText>
          </MenuItem>
        )}
        {menuRow?.status === "PENDING_APPROVAL" && (
          <MenuItem
            onClick={() => {
              closeMenu();
              handleApproveSchedule(menuRow.id);
            }}
          >
            <ListItemIcon><VerifiedUserOutlined fontSize="small" color="success" /></ListItemIcon>
            <ListItemText>Approve Schedule</ListItemText>
          </MenuItem>
        )}
        {menuRow?.status === "ACTIVE" && (
          <MenuItem
            onClick={() => {
              closeMenu();
              handleRetireSchedule(menuRow.id);
            }}
          >
            <ListItemIcon><RemoveCircleOutline fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>Retire Schedule</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* ── Modal: View Schedule Tiers ───────────────────────────────────── */}
      <Dialog open={Boolean(viewSchedule)} onClose={() => setViewSchedule(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Tariff Bands: {viewSchedule?.name}
            </Typography>
            <Typography variant="caption" sx={{ color: colors.grey[400] }}>
              Schedule #{viewSchedule?.id} &bull; Status: {viewSchedule?.status}
            </Typography>
          </Box>
          {viewSchedule && getStatusChip(viewSchedule.status)}
        </DialogTitle>
        <DialogContent dividers>
          {viewLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : viewTiers.length === 0 ? (
            <Typography variant="body2" sx={{ textAlign: "center", py: 4, color: colors.grey[400] }}>
              No tier bands defined for this schedule.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Band Range (KES)</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: "#1766A2" }}>
                      Sababisha Charge
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: "#ed6c02" }}>
                      Safaricom Charge
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: "#22bf72" }}>
                      Total Charge
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {viewTiers.map((t, idx) => (
                    <TableRow key={t.id || idx}>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {formatKES(t.amountFrom)} &ndash; {formatKES(t.amountTo)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: "#1766A2", fontWeight: 700 }}>
                        {formatKES(t.sababishaCharge)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: "#ed6c02", fontWeight: 700 }}>
                        {formatKES(t.mpesaCharge)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: "#22bf72", fontWeight: 800 }}>
                        {formatKES(t.totalCharge ?? t.sababishaCharge + t.mpesaCharge)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewSchedule(null)} sx={{ textTransform: "none" }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Modal: Create Disbursement Fee Schedule ──────────────────────── */}
      <Dialog open={createOpen} onClose={handleCloseModal} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {isEditing
              ? `Edit Disbursement Fee Schedule #${editingId}`
              : "Create Disbursement Fee Schedule (Standard Tariff)"}
          </Typography>
          <Typography variant="caption" sx={{ color: colors.grey[400] }}>
            Configure loan disbursement fee bands stating both Sababisha and Safaricom charges.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Schedule Name *"
                placeholder="e.g. Standard Disbursement Tariff 2026"
                value={newScheduleName}
                disabled={isEditing}
                onChange={(e) => setNewScheduleName(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Effective From Date"
                InputLabelProps={{ shrink: true }}
                value={newEffectiveFrom}
                disabled={isEditing}
                onChange={(e) => setNewEffectiveFrom(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Notes / Governance Reference"
                placeholder="Reference to committee approval or contract addendum"
                value={newNotes}
                disabled={isEditing}
                onChange={(e) => setNewNotes(e.target.value)}
              />
            </Grid>
          </Grid>

          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5, flexWrap: "wrap", gap: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1766A2" }}>
                Tariff Band Configuration ({newTiers.length} bands)
              </Typography>
              {(() => {
                const max = parseFloat(newTiers[newTiers.length - 1]?.amountTo) || 0;
                return max === 1000000 ? (
                  <Chip
                    label="Max Limit: KES 1,000,000 ✓"
                    size="small"
                    color="success"
                    sx={{ fontWeight: 700, fontSize: "0.75rem" }}
                  />
                ) : (
                  <Chip
                    label={`Max: KES ${max.toLocaleString()} (Target: 1,000,000)`}
                    size="small"
                    color={max < 1000000 ? "warning" : "error"}
                    sx={{ fontWeight: 700, fontSize: "0.75rem" }}
                  />
                );
              })()}
            </Box>
            <Stack direction="row" spacing={1}>
              {activeTiers.length > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AutoModeOutlined />}
                  onClick={handleCloneActiveTiers}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  Clone Active Schedule Bands
                </Button>
              )}
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddOutlined />}
                onClick={handleAddTierRow}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Add Band Row
              </Button>
            </Stack>
          </Box>

          <TableContainer sx={{ maxHeight: 350 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Band From (KES)</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Band To (KES)</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: "#1766A2" }}>Sababisha Charge (KES)</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: "#ed6c02" }}>Safaricom Charge (KES)</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: "#22bf72" }}>Total Charge (KES)</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Remove</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {newTiers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" sx={{ color: colors.grey[400], mb: 1 }}>
                        No tariff bands configured yet.
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddOutlined />}
                        onClick={handleAddTierRow}
                        sx={{ textTransform: "none", fontWeight: 600 }}
                      >
                        Add First Tariff Band
                      </Button>
                    </TableCell>
                  </TableRow>
                ) : (
                  newTiers.map((tier, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <TextField
                          size="small"
                          type="text"
                          inputProps={{ inputMode: "decimal" }}
                          value={tier.amountFrom !== undefined && tier.amountFrom !== null ? tier.amountFrom : "0"}
                          onChange={(e) => handleTierChange(idx, "amountFrom", e.target.value)}
                          onFocus={(e) => e.target.select()}
                          onBlur={(e) => {
                            if (e.target.value === "") handleTierChange(idx, "amountFrom", "0");
                          }}
                          placeholder="0"
                          sx={{ width: 120 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="text"
                          inputProps={{ inputMode: "decimal" }}
                          value={tier.amountTo !== undefined && tier.amountTo !== null ? tier.amountTo : ""}
                          onChange={(e) => handleTierChange(idx, "amountTo", e.target.value)}
                          onFocus={(e) => e.target.select()}
                          onBlur={(e) => {
                            if (e.target.value === "") handleTierChange(idx, "amountTo", "0");
                          }}
                          placeholder="0"
                          sx={{ width: 120 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="text"
                          inputProps={{ inputMode: "decimal" }}
                          value={tier.sababishaCharge !== undefined && tier.sababishaCharge !== null ? tier.sababishaCharge : "0"}
                          onChange={(e) => handleTierChange(idx, "sababishaCharge", e.target.value)}
                          onFocus={(e) => e.target.select()}
                          onBlur={(e) => {
                            if (e.target.value === "") handleTierChange(idx, "sababishaCharge", "0");
                          }}
                          placeholder="0"
                          sx={{ width: 120 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="text"
                          inputProps={{ inputMode: "decimal" }}
                          value={tier.mpesaCharge !== undefined && tier.mpesaCharge !== null ? tier.mpesaCharge : "0"}
                          onChange={(e) => handleTierChange(idx, "mpesaCharge", e.target.value)}
                          onFocus={(e) => e.target.select()}
                          onBlur={(e) => {
                            if (e.target.value === "") handleTierChange(idx, "mpesaCharge", "0");
                          }}
                          placeholder="0"
                          sx={{ width: 120 }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 800, color: "#22bf72" }}>
                        {formatKES(tier.totalCharge)}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" color="error" onClick={() => handleRemoveTierRow(idx)}>
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}

              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={handleCloseModal} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateSchedule}
            disabled={creating}
            startIcon={creating ? <CircularProgress size={16} /> : <CheckCircleOutline />}
            sx={{
              backgroundColor: "#1766A2",
              color: "#fff",
              fontWeight: 700,
              textTransform: "none",
              px: 3,
            }}
          >
            {creating
              ? isEditing
                ? "Updating Schedule..."
                : "Creating Schedule..."
              : isEditing
              ? "Update & Submit for Approval"
              : "Save Schedule (Draft)"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DisbursementFeeSchedules;